import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, HeartPulse, Accessibility, Bus, Building2, Users2, CheckCircle2 } from "lucide-react";

const SOLUTIONS = [
  { icon: HeartPulse, title: "Non-Emergency Medical Transport", desc: "Reliable rides to dialysis, treatment and appointments with the documentation agencies expect.", points: ["Recurring route scheduling", "Mobility-aware trip assignment", "Trip evidence & audit trail"] },
  { icon: Accessibility, title: "Paratransit & Accessible Transport", desc: "Serve riders with wheelchair and stretcher needs while keeping every trip accountable.", points: ["Wheelchair & stretcher vehicle tracking", "Lift & equipment incident logging", "On-time performance reporting"] },
  { icon: Users2, title: "Senior & Community Transport", desc: "Keep community members moving with dependable dispatch and clear oversight.", points: ["Simple dispatch & reassignment", "Live driver locations", "Caregiver-ready accountability"] },
  { icon: Building2, title: "Agency & Broker Fleets", desc: "Manage multi-driver fleets under contract with compliance and cost visibility.", points: ["Driver & vehicle compliance", "Repair cost tracking", "Operational reporting"] },
  { icon: Bus, title: "Shuttle & Route Operations", desc: "Run fixed and on-demand routes with live visibility and late-trip prevention.", points: ["Schedule building", "Late-risk alerts", "Fleet maintenance tracking"] },
];

export default function SolutionsPage() {
  const navigate = useNavigate();
  return (
    <div data-testid="solutions-page">
      <div className="bg-slate-950 pt-36 pb-20 text-white">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-blue-400">Solutions</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
            Purpose-built for transportation providers
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            RideStamp adapts to how your operation actually runs — whatever and whoever you move.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="grid gap-6 lg:grid-cols-2">
          {SOLUTIONS.map((s, i) => (
            <div key={s.title} className={`rounded-2xl border border-slate-200 bg-white p-8 transition-all hover:shadow-xl hover:shadow-slate-200/60 ${i === 0 ? "lg:col-span-2 lg:flex lg:items-center lg:gap-10" : ""}`}
              data-testid={`solution-card-${i}`}>
              <div className={i === 0 ? "lg:w-1/2" : ""}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-800 text-white"><s.icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-slate-600">{s.desc}</p>
              </div>
              <ul className={`mt-5 space-y-2.5 ${i === 0 ? "lg:mt-0 lg:w-1/2" : ""}`}>
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-6 rounded-3xl bg-gradient-to-br from-blue-900 to-slate-950 p-10 text-center sm:flex-row sm:text-left lg:p-14">
          <div>
            <h2 className="font-display text-2xl font-bold text-white lg:text-3xl">Not sure which fits your operation?</h2>
            <p className="mt-2 text-slate-300">Talk to our team and we'll map RideStamp to your service model.</p>
          </div>
          <Button onClick={() => navigate("/contact")} data-testid="solutions-cta"
            className="shrink-0 gap-2 rounded-full bg-white px-7 py-6 text-base font-semibold text-slate-900 hover:bg-slate-100">
            Talk to Sales <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
